import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import PasswordInput from "@/components/base/PasswordInput";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { buildCallbackUrl } from "@/lib/auth-paths";

type Mode = "login" | "register" | "forgot";

export default function Login() {
  const navigate = useNavigate();
  const { session } = useAuth();

  const [mode, setMode] = useState<Mode>("login");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Redirect already-logged-in users
  useEffect(() => {
    if (session && mode !== "forgot") {
      navigate("/profile", { replace: true });
    }
  }, [session, mode, navigate]);

  const switchMode = (m: Mode) => {
    setMode(m);
    setError("");
    setInfo("");
  };

  const handleLogin = async () => {
    setError("");
    setInfo("");
    if (!email.trim() || !password) {
      setError("Vui lòng nhập đầy đủ email và mật khẩu.");
      return;
    }
    setLoading(true);
    try {
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (loginError) {
        setError(
          loginError.message === "Invalid login credentials"
            ? "Email hoặc mật khẩu không đúng."
            : loginError.message,
        );
        return;
      }
      navigate("/profile");
    } catch {
      setError("Có lỗi xảy ra. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setError("");
    setInfo("");
    if (!name.trim() || !phone.trim() || !email.trim() || !password) {
      setError("Vui lòng nhập đầy đủ thông tin đăng ký.");
      return;
    }
    if (password.length < 6) {
      setError("Mật khẩu phải có ít nhất 6 ký tự.");
      return;
    }
    setLoading(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            name: name.trim(),
            phone: phone.trim(),
          },
          emailRedirectTo: buildCallbackUrl(),
        },
      });

      if (signUpError) {
        setError(signUpError.message);
        return;
      }

      // If auto-confirmed, session is returned immediately
      if (data.session?.user) {
        navigate("/profile");
        return;
      }

      // Email confirmation required
      setInfo(
        "Đăng ký thành công! Vui lòng kiểm tra email để xác nhận tài khoản, sau đó đăng nhập.",
      );
    } catch {
      setError("Có lỗi xảy ra. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    setError("");
    setInfo("");
    if (!recoveryEmail.trim()) {
      setError("Vui lòng nhập email của bạn.");
      return;
    }
    setLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        recoveryEmail.trim(),
        { redirectTo: buildCallbackUrl("/auth/reset-password") },
      );
      if (resetError) {
        setError(resetError.message);
        return;
      }
      setInfo(
        "Đã gửi link đặt lại mật khẩu đến email của bạn. Vui lòng mở email và bấm vào link để tiếp tục.",
      );
    } catch {
      setError("Có lỗi xảy ra. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="w-full min-h-screen bg-background-50 pt-28 md:pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
              <i
                className={`${
                  mode === "forgot"
                    ? "ri-key-2-line"
                    : "ri-user-heart-line"
                } text-2xl`}
              />
            </div>
            <h1 className="font-display text-3xl font-bold text-foreground-950 mb-2">
              {mode === "login"
                ? "Đăng nhập"
                : mode === "register"
                  ? "Đăng ký tài khoản"
                  : "Quên mật khẩu"}
            </h1>
            <p className="text-sm text-foreground-600 font-body">
              {mode === "login"
                ? "Chào mừng bạn quay lại với Xanh Wellness"
                : mode === "register"
                  ? "Tạo tài khoản để quản lý lịch hẹn dễ dàng hơn"
                  : "Nhập email để nhận link đặt lại mật khẩu"}
            </p>
          </div>

          {/* Mode toggle */}
          <div className="flex p-1 bg-background-100 rounded-full mb-6">
            {(["login", "register"] as const).map((m) => (
              <button
                key={m}
                onClick={() => switchMode(m)}
                className={`flex-1 px-1 py-2 rounded-full text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  mode === m
                    ? "bg-background-50 text-primary-700"
                    : "text-foreground-600 hover:text-foreground-800"
                }`}
              >
                {m === "login" ? "Đăng nhập" : "Đăng ký"}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-lg border border-background-200/70 p-5 md:p-6">
            {/* Forgot password form */}
            {mode === "forgot" ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleForgot();
                }}
                className="space-y-4"
              >
                <div>
                  <label
                    htmlFor="recovery-email"
                    className="block text-sm font-label font-medium text-foreground-800 mb-1.5"
                  >
                    Email
                  </label>
                  <input
                    id="recovery-email"
                    type="email"
                    name="email"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
                  />
                </div>

                {error && (
                  <div className="px-4 py-3 bg-accent-50 border border-accent-200 rounded-md text-sm text-accent-700 font-body flex items-start gap-2">
                    <i className="ri-error-warning-line mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                {info && (
                  <div className="px-4 py-3 bg-primary-50 border border-primary-200 rounded-md text-sm text-primary-700 font-body flex items-start gap-2">
                    <i className="ri-information-line mt-0.5" />
                    <span>{info}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2" />
                      Đang xử lý...
                    </>
                  ) : (
                    "Gửi link đặt lại mật khẩu"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="w-full text-center text-sm text-foreground-500 font-body hover:text-foreground-800 cursor-pointer transition-colors"
                >
                  Quay lại đăng nhập
                </button>
              </form>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (mode === "login") handleLogin();
                  else handleRegister();
                }}
                className="space-y-4"
              >
                {mode === "register" && (
                  <>
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-label font-medium text-foreground-800 mb-1.5"
                      >
                        Họ và tên
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Nhập họ và tên"
                        className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-label font-medium text-foreground-800 mb-1.5"
                      >
                        Số điện thoại
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="VD: 0909 123 456"
                        className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-label font-medium text-foreground-800 mb-1.5"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label
                      htmlFor="password"
                      className="block text-sm font-label font-medium text-foreground-800"
                    >
                      Mật khẩu
                    </label>
                    {mode === "login" && (
                      <button
                        type="button"
                        onClick={() => switchMode("forgot")}
                        className="text-xs text-primary-700 font-medium hover:underline cursor-pointer"
                      >
                        Quên mật khẩu?
                      </button>
                    )}
                  </div>
                  <PasswordInput
                    id="password"
                    name="password"
                    value={password}
                    onChange={setPassword}
                    placeholder="Ít nhất 6 ký tự"
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                  />
                </div>

                {error && (
                  <div className="px-4 py-3 bg-accent-50 border border-accent-200 rounded-md text-sm text-accent-700 font-body flex items-start gap-2">
                    <i className="ri-error-warning-line mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                {info && (
                  <div className="px-4 py-3 bg-primary-50 border border-primary-200 rounded-md text-sm text-primary-700 font-body flex items-start gap-2">
                    <i className="ri-information-line mt-0.5" />
                    <span>{info}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2" />
                      Đang xử lý...
                    </>
                  ) : mode === "login" ? (
                    "Đăng nhập"
                  ) : (
                    "Tạo tài khoản"
                  )}
                </button>
              </form>
            )}
          </div>

          {mode !== "forgot" && (
            <p className="text-center text-sm text-foreground-500 font-body mt-6">
              {mode === "login" ? "Bạn chưa có tài khoản?" : "Đã có tài khoản?"}{" "}
              <button
                onClick={() => switchMode(mode === "login" ? "register" : "login")}
                className="text-primary-700 font-medium hover:underline cursor-pointer"
              >
                {mode === "login" ? "Đăng ký ngay" : "Đăng nhập"}
              </button>
            </p>
          )}

          <p className="text-center text-sm text-foreground-400 font-body mt-2">
            Hoặc đặt lịch không cần đăng nhập{" "}
            <Link
              to="/booking"
              className="text-primary-700 font-medium hover:underline"
            >
              tại đây
            </Link>
          </p>
        </div>
      </div>
    </MainLayout>
  );
}